/**
 * The review queue's warnings cell: what a draft needs looked at before it is published.
 *
 * "Ready" when the body passes every soft check, a count with the messages on hover otherwise.
 * None of these block a publish (the publish gate is separate and stricter); they are the things a
 * reviewer would otherwise only find by opening each draft.
 */
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { Box, CircularProgress, Tooltip, Typography } from '@mui/material';
import React from 'react';

import type { DraftWarningState } from './reviewWarnings';

const DraftWarningsCell: React.FC<{ state?: DraftWarningState }> = ({ state }) => {
  if (!state || state.loading) {
    return (
      <Box className="guides-review__cell is-loading" aria-label="Checking draft">
        <CircularProgress size={14} />
      </Box>
    );
  }
  if (state.error) {
    return (
      <Typography className="guides-review__cell is-muted" variant="caption">
        -
      </Typography>
    );
  }
  if (state.warnings.length === 0) {
    return (
      <Box className="guides-review__cell is-ready">
        <CheckCircleOutlineRoundedIcon fontSize="small" className="guides-review__ready-icon" />
        <Typography component="span" className="guides-review__ready-text">
          Ready
        </Typography>
      </Box>
    );
  }
  return (
    <Tooltip
      title={
        <Box component="ul" className="guides-review__tip">
          {state.warnings.map((w) => (
            <li key={w.id}>{w.message}</li>
          ))}
        </Box>
      }
    >
      <Box className="guides-review__cell is-warn" aria-label={`${state.warnings.length} warning(s)`}>
        <WarningAmberRoundedIcon fontSize="small" className="guides-review__warn-icon" />
        <Typography component="span" className="guides-review__warn-text">
          {state.warnings.length} to review
        </Typography>
      </Box>
    </Tooltip>
  );
};

export default DraftWarningsCell;
